'use client'

import { Loader } from '@vinted/web-ui'
import { useEffect } from 'react'

import { redirectToPage } from '@marketplace-web/browser/browser-navigation-util'
import { logMessage, logWarning } from '@marketplace-web/observability/logging-util'

import { toParams } from '@marketplace-web/browser/url-util'
import { refreshSessionTokens } from '@marketplace-web/session-management/session-management-interceptor-util'

import { TEN_MINUTES_MS, FIVE_SECONDS_MS } from '../../constants/date'

import {
  SESSION_REFRESH_EXPIRATION_MS,
  SESSION_REFRESH_STATUS_LOCAL_STORAGE_KEY,
} from '../../constants'

import {
  getIsSessionRefreshedRecently,
  getIsSessionRefreshPending,
  startSessionRefreshStatus,
  stopSessionRefreshStatus,
} from '../../utils/session-refresh-status'
import { getRefUrlContextless } from '../../utils/url'
import {
  incrementSessionRefreshPageLoad,
  incrementSessionRefreshPendingStatusTimeout,
} from '../../observability/session-refresh-metrics'
import { EXPIRE_COOKIES_URL } from '../../constants/routes'

const checkIfRefreshingMultipleTimes = (anonId: string) => {
  try {
    const refreshStatus = sessionStorage.getItem(SESSION_REFRESH_STATUS_LOCAL_STORAGE_KEY)

    if (!refreshStatus || parseInt(refreshStatus, 10) - TEN_MINUTES_MS > Date.now()) {
      sessionStorage.setItem(SESSION_REFRESH_STATUS_LOCAL_STORAGE_KEY, Date.now().toString())
    } else {
      logWarning(
        `Refreshing token multiple times: ${String(toParams(window.location.search).ref_url)}`,
        {
          extra: JSON.stringify({ anonId }),
          feature: 'session-refresh',
        },
      )
    }
  } catch {
    //
  }
}

type Props = {
  anonId: string
}

const SessionRefresh = ({ anonId }: Props) => {
  useEffect(() => incrementSessionRefreshPageLoad(), [])

  useEffect(() => checkIfRefreshingMultipleTimes(anonId), [anonId])

  useEffect(() => {
    let timeoutRef: NodeJS.Timeout
    const refUrl = getRefUrlContextless()

    const redirectOnFinishedRefresh = (event?: StorageEvent) => {
      if (event && event.key !== SESSION_REFRESH_STATUS_LOCAL_STORAGE_KEY) return
      if (getIsSessionRefreshPending()) return

      logMessage('session refresh finished', {
        feature: 'session-refresh',
        extra: JSON.stringify({ anonId }),
      })

      window.removeEventListener('storage', redirectOnFinishedRefresh)
      clearTimeout(timeoutRef)
      redirectToPage(refUrl)
    }

    const handleSessionRefresh = async () => {
      startSessionRefreshStatus()

      const response = await refreshSessionTokens()

      stopSessionRefreshStatus()

      if (!('access_token' in response)) {
        logMessage(`failure refresh response: ${response.status}`, {
          extra: JSON.stringify({ anonId }),
          feature: 'session-refresh',
        })

        redirectToPage(EXPIRE_COOKIES_URL)

        return
      }

      logMessage('successful refresh response', {
        extra: JSON.stringify({ anonId }),
        feature: 'session-refresh',
      })

      redirectOnFinishedRefresh()
    }

    if (getIsSessionRefreshedRecently()) {
      setTimeout(() => {
        logMessage('retry refresh', {
          feature: 'session-refresh',
          extra: JSON.stringify({ anonId }),
        })

        redirectToPage(refUrl)
      }, FIVE_SECONDS_MS)

      return undefined
    }

    const handlePendingStatusTimeout = () => {
      incrementSessionRefreshPendingStatusTimeout()
      handleSessionRefresh()
    }

    if (getIsSessionRefreshPending()) {
      window.addEventListener('storage', redirectOnFinishedRefresh)

      timeoutRef = setTimeout(handlePendingStatusTimeout, SESSION_REFRESH_EXPIRATION_MS)

      return () => window.removeEventListener('storage', redirectOnFinishedRefresh)
    }

    handleSessionRefresh()

    return undefined
  }, [anonId])

  return (
    <div className="u-flexbox u-justify-content-center u-align-items-center u-fill-viewport">
      <Loader size="large" />
    </div>
  )
}

export default SessionRefresh
